import classNames from "classnames";
import styles from "./AscCircularProgressBarWithIcon.module.scss";
import { AscBadgeColor } from "@ascendnow/shared";
import { useResponsive } from "@ascendnow/ascend-components";

export interface AscCircularProgressBarWithIconProps {
  id?: string;
  percentage: number;
  color: AscBadgeColor;
  number?: number;
  locked?: boolean;
  className?: string;
}

function AscCircularProgressBarWithIcon({
  id = "",
  percentage,
  color,
  number,
  locked,
  className,
}: AscCircularProgressBarWithIconProps) {
  const { isMedium } = useResponsive();

  const wavesGradient = (
    color: AscCircularProgressBarWithIconProps["color"]
  ) => {
    switch (color) {
      case "PURPLE":
        return {
          color1: "var(--purple)",
          color2: "var(--lightBlue)",
        };
      case "TEAL":
        return {
          color1: "var(--purple)",
          color2: "var(--lightBlue)",
        };

      case "GREEN":
        return {
          color1: "var(--lightGreen)",
          color2: "var(--lightBlue)",
        };

      case "ORANGE":
        return {
          color1: "var(--yellowOrange)",
          color2: "var(--pink)",
        };
      case "PINK":
        return {
          color1: "var(--pink)",
          color2: "var(--yellowOrange)",
        };
    }
  };

  const brandColor = (color: AscCircularProgressBarWithIconProps["color"]) => {
    switch (color) {
      case "PURPLE":
        return "var(--purple)";
      case "TEAL":
        return "var(--lightBlue)";
      case "GREEN":
        return "var(--lightGreen)";
      case "ORANGE":
        return "var(--yellowOrange)";
      case "PINK":
        return "var(--pink)";
    }
  };

  const darkColor = (color: AscCircularProgressBarWithIconProps["color"]) => {
    switch (color) {
      case "PURPLE":
        return "var(--darkPurple)";
      case "TEAL":
        return "var(--darkSecondaryBlue)";
      case "GREEN":
        return "var(--darkGreen)";
      case "ORANGE":
        return "var(--darkOrange)";
      case "PINK":
        return "var(--darkPink)";
    }
  };

  return (
    <div className={classNames(styles.container, className)}>
      {number && (
        <div className={styles.numberWrapper}>
          <div className={styles.number}>{number}</div>
        </div>
      )}
      <svg
        width={isMedium ? "130" : "88"}
        height={isMedium ? "144" : "100"}
    
        viewBox="0 0 130 144"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M62.5 135C97.0178 135 125 107.018 125 72.5V64H0V72.5C0 107.018 27.9822 135 62.5 135Z"
          fill={locked ? "var(--gray700)" : darkColor(color)}
        />
        <path
          d="M115 62.5C115 91.4949 91.4949 115 62.5 115C33.5051 115 10 91.4949 10 62.5C10 33.5051 33.5051 10 62.5 10C91.4949 10 115 33.5051 115 62.5Z"
          fill={locked ? "var(--gray500)" : `url(#wavesGradient${id})`}
        />
        <mask
          id="mask0_305_111464"
          style={{ maskType: "alpha" }}
          maskUnits="userSpaceOnUse"
          x="10"
          y="10"
          width="105"
          height="106"
        >
          <path
            d="M115 62.7008C115 91.8066 91.4949 115.402 62.5 115.402C33.5051 115.402 10 91.8066 10 62.7008C10 33.5949 33.5051 10 62.5 10C91.4949 10 115 33.5949 115 62.7008Z"
            fill="white"
          />
        </mask>
        <g mask="url(#mask0_305_111464)">
          <path
            d="M5.3067 119.936L5.2 120.236L1 66.6915C20.74 29.1686 64.84 64.1617 84.16 71.7506C99.616 77.8218 123.08 58.8214 129.1 49.4055L130.78 59.9458C107.26 84.8206 90.04 97.8902 47.2 89.0365C18.4379 83.0922 10.0839 106.532 5.3067 119.936Z"
            fill="white"
            fillOpacity="0.1"
          />
          <path
            d="M92.56 40.9734C77.776 40.9734 68.2 11.4609 37.12 11.4609L20.32 27.0604C54.76 19.0498 67.696 41.0527 84.16 46.4543C100.624 51.8558 121.82 33.3865 123.22 27.0604L120.7 16.5202C114.82 27.0604 111.04 40.9734 92.56 40.9734Z"
            fill="white"
            fillOpacity="0.1"
          />
        </g>
        <path
          d="M120 62.5C120 94.2564 94.2564 120 62.5 120C30.7436 120 5 94.2564 5 62.5C5 30.7436 30.7436 5 62.5 5C84.3624 5 103.375 17.2012 113.1 35.1657C117.501 43.2957 120 52.606 120 62.5Z"
          stroke="#ffffff"
          strokeWidth="9.5"
        />
        {!locked && (
          <path
            d="M86 114.5C85 115.5 76 120 62.5 120C30.7436 120 5 94.2564 5 62.5C5 30.7436 30.7436 5 62.5 5C84.3624 5 103.375 17.2012 113.1 35.1657C130 69.5 110.5 95 107.5 98"
            stroke={brandColor(color)}
            strokeWidth={percentage === 0 ? 0 : 10}
            strokeLinecap="round"
            style={{ strokeDasharray: `${(percentage * 324) / 100} 342` }}
          />
        )}
        <path
          d="M107 144C119.703 144 130 133.703 130 121C130 116.868 130 118 130 113L84.0013 113C84.0005 118 84 116.571 84 121C84 133.703 94.2975 144 107 144Z"
          fill={locked ? "var(--gray700)" : darkColor(color)}
        />
        <ellipse
          cx="107"
          cy="113"
          rx="23"
          ry="23"
          transform="rotate(-90 107 113)"
          fill={
            percentage < 100 ? "white" : locked ? "white" : brandColor(color)
          }
        />

        {locked ? (
          <>
            <path
              xmlns="http://www.w3.org/2000/svg"
              d="M101 111V109C101 105.69 102 103 107 103C112 103 113 105.69 113 109V111"
              stroke="#888EA7"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              xmlns="http://www.w3.org/2000/svg"
              d="M107 119.5C108.381 119.5 109.5 118.381 109.5 117C109.5 115.619 108.381 114.5 107 114.5C105.619 114.5 104.5 115.619 104.5 117C104.5 118.381 105.619 119.5 107 119.5Z"
              stroke="#888EA7"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              xmlns="http://www.w3.org/2000/svg"
              d="M112 123H102C98 123 97 122 97 118V116C97 112 98 111 102 111H112C116 111 117 112 117 116V118C117 122 116 123 112 123Z"
              stroke="#888EA7"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        ) : (
          <path
            d="M98 113.133L104.133 119.267L116.4 107"
            stroke={percentage < 100 ? brandColor(color) : "white"}
            strokeWidth="2.08336"
            strokeLinecap="round"
          />
        )}

        <path
          d="M82 36L83.0013 38.9987L86 40L83.0013 41.0013L82 44L80.9987 41.0013L78 40L80.9987 38.9987L82 36Z"
          fill="white"
          fillOpacity="0.8"
        />
        <path
          d="M96 66L98.0025 71.9975L104 74L98.0025 76.0025L96 82L93.9975 76.0025L88 74L93.9975 71.9975L96 66Z"
          fill="white"
          fillOpacity="0.8"
        />
        <path
          d="M27 68L28.2516 71.7484L32 73L28.2516 74.2516L27 78L25.7484 74.2516L22 73L25.7484 71.7484L27 68Z"
          fill="white"
          fillOpacity="0.8"
        />
        <defs>
          <linearGradient
            id={`wavesGradient${id}`}
            x1="63"
            y1="202.15"
            x2="63"
            y2="-39.35"
            gradientUnits="userSpaceOnUse"
          >
            <stop
              stopColor={
                locked ? "var(--gray500)" : wavesGradient(color).color1
              }
            />
            <stop
              offset="1"
              stopColor={
                locked ? "var(--gray500)" : wavesGradient(color).color2
              }
            />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

export default AscCircularProgressBarWithIcon;
